import React from 'react';
import { Link } from 'react-router-dom';
import SitePage, { SectionHeader, GLYPHS } from './SiteChrome';
import AppsBand from './AppsBand';

// Services (spec §5): the five pillars precisely, then the two ways in.
// All copy final from the spec. Each block carries its anchor id; the
// "Discuss this" link preselects that pillar on the contact flow.
// Layout: alternating editorial spreads — generated pillar imagery with a
// mask-wipe reveal, oversized ghost numerals, glyphs that draw themselves.

const SERVICES = [
  {
    anchor: 'web',
    img: '/ext/media/pillar-web.jpg',
    imgAlt: 'Site design in progress: laptop, wireframe sheets, sea light',
    name: 'Website & application design, build, and maintenance',
    promise: 'Designed, built, and kept alive by the same hands.',
    deliver: 'Site or app design (desktop and mobile); front-end build on a clean, fast stack; booking, forms, and the small flows that make a site do work; hosting and domain setup; ongoing maintenance — updates, fixes, small improvements — so the site never freezes on launch day.',
    not: 'An enterprise platform build, a marketplace, or software with its own product team. If it needs a product team, we\u2019ll say so before anyone spends money.',
    start: 'An existing site that\u2019s almost right, or a clear brief for a focused new one.',
  },
  {
    anchor: 'identity',
    img: '/ext/media/pillar-identity.jpg',
    imgAlt: 'Mark studies in blue ink with palette swatches on paper',
    name: 'Logo design and brand aesthetics',
    promise: 'The mark, the palette, the type, and the rules that hold them together.',
    deliver: 'Wordmark and lockups; color and type system; button and component aesthetics for the site; application to the physical stuff — vehicle, uniform, signage, packaging — where the business lives; a short guardrails document so the system survives contact with the real world.',
    not: 'A 90-page brand book nobody opens, or endless mood boards. We decide, then apply.',
    start: 'A business that looks assembled from parts and wants to look like one thing.',
  },
  {
    anchor: 'presence',
    img: '/ext/media/pillar-presence.jpg',
    imgAlt: 'Turquoise megaphone on a plinth, signal arcs on a plaster wall',
    name: 'Online presence and advertising',
    promise: 'Profiles, listings, and campaigns that carry the brand, not dilute it.',
    deliver: 'Google Business and listing cleanup; social profile alignment — same name, same look, same offer everywhere; campaign structure and monthly ad flights; offer and package framing that survives an auction; a short monthly note on what ran and what\u2019s next.',
    not: 'Guaranteed leads, growth hacking, or a media budget (ad spend is always the client\u2019s, held in the client\u2019s accounts).',
    start: 'A good business that\u2019s invisible, inconsistent, or paying for ads that don\u2019t look like the brand.',
  },
  {
    anchor: 'content',
    img: '/ext/media/pillar-content.jpg',
    imgAlt: 'Film camera and contact prints of Mediterranean scenes',
    gallery: [
      { src: '/ext/media/photo-boat.jpg', alt: 'Wooden boat on clear turquoise water' },
      { src: '/ext/media/photo-alley.jpg', alt: 'Whitewashed steps, turquoise railing, olive tree' },
      { src: '/ext/media/photo-table.jpg', alt: 'Olive branch and ceramic cups in morning light' },
    ],
    name: 'Photography and content creation',
    promise: 'Real pictures of the real work. No stock.',
    deliver: 'Shot lists and style frames before anyone lifts a camera; photography direction and shoots; a working content system — a library the site and channels draw from monthly; short-form assets sized for where they run.',
    not: 'A one-time shoot that ages out in a quarter, or AI-generated stand-ins for work you actually do.',
    start: 'A site or feed running on generic imagery while the real work goes unphotographed.',
  },
  {
    anchor: 'copy',
    img: '/ext/media/pillar-copy.jpg',
    imgAlt: 'Letterpress type, fountain pen, and turquoise ink pot on plaster',
    name: 'Brand messaging and copy',
    promise: 'What you say, everywhere you say it.',
    deliver: 'Positioning lines and page copy; service and package descriptions people actually read; booking and form microcopy; seasonal and offer lines for campaigns; one voice across site, profiles, and ads.',
    not: 'SEO sludge or a content calendar for its own sake.',
    start: 'A business that explains itself differently on every channel.',
  },
];

function ServiceBlock({ s, i }) {
  const Glyph = GLYPHS[s.anchor];
  return (
    <section className="ac-svc" id={s.anchor}>
              <div className="ac-svc-media ac-reveal-wipe">
                <img src={s.img} alt={s.imgAlt} loading="lazy" />
                <span className="ac-ghost-num" aria-hidden="true">0{i + 1}</span>
              </div>
              <div className="ac-svc-copy">
                {Glyph && <Glyph className="ac-svc-glyph" />}
                <h3 className="ac-reveal">{s.name}</h3>
                <p className="ac-svc-promise ac-reveal" style={{ '--d': '0.08s' }}>{s.promise}</p>
                <dl>
                  <div className="ac-reveal" style={{ '--d': '0.14s' }}><dt>What we deliver</dt><dd>{s.deliver}</dd></div>
                  <div className="ac-reveal" style={{ '--d': '0.2s' }}><dt>What it is not</dt><dd>{s.not}</dd></div>
                  <div className="ac-reveal" style={{ '--d': '0.26s' }}><dt>Typical start point</dt><dd>{s.start}</dd></div>
                </dl>
                <p className="ac-reveal" style={{ '--d': '0.3s' }}>
                  <Link className="ac-textlink" to={`/contact?interest=${s.anchor}`}>
                    Discuss this with us →
                  </Link>
                </p>
              </div>
              {s.gallery && (
                <div className="ac-photo-strip">
                  <span className="ac-kicker">The eye, in practice</span>
                  <div className="ac-photo-strip-grid">
                    {s.gallery.map((g, gi) => (
                      <figure className="ac-reveal-wipe" style={{ '--d': `${gi * 0.12}s` }} key={g.src}>
                        <img src={g.src} alt={g.alt} loading="lazy" />
                      </figure>
                    ))}
                  </div>
                  <p className="ac-reveal">
                    <Link className="ac-textlink" to="/work#photography">Sixteen more in the studio index →</Link>
                  </p>
                </div>
              )}
    </section>
  );
}

export default function Services() {
  return (
    <SitePage>
      <section className="ac-section" style={{ paddingBottom: 0 }}>
        <div className="ac-wrap">
          <SectionHeader kicker="Services" title="Five pillars. One system." />
        </div>
      </section>

      {/* Pillar #1, then the apps capability it implies, then the rest */}
      <div className="ac-wrap">
        <ServiceBlock s={SERVICES[0]} i={0} />
      </div>

      {/* Apps, on a foundation — the spotlight behind pillar #1 */}
      <AppsBand />

      <div className="ac-wrap">
        {SERVICES.slice(1).map((s, i) => (
          <ServiceBlock s={s} i={i + 1} key={s.anchor} />
        ))}
      </div>

      {/* 5.6 Engagements — two ways in, priced on /pricing */}
      <section className="ac-section ac-section-wash" style={{ marginTop: 'clamp(40px, 6vw, 64px)' }}>
        <div className="ac-wrap">
          <SectionHeader kicker="Engagements" title="Two ways in." />
          <div className="ac-engage">
            <div className="ac-engage-card ac-reveal">
              <h3>Foundation<em>.</em></h3>
              <p>A project. Identity, site or app, messaging, and the first content set, delivered as one system. Scoped and quoted after inquiry.</p>
            </div>
            <div className="ac-engage-card ac-reveal" style={{ '--d': '0.12s' }}>
              <h3>Presence<em>.</em></h3>
              <p>A six-month follow-on. Maintenance, monthly content, advertising, and copy care, with a defined monthly cap.</p>
            </div>
          </div>
          <p style={{ marginTop: 40 }}>
            <Link className="ac-textlink" to="/pricing">Starting prices, stated up front →</Link>
            {' '}&nbsp;{' '}
            <Link className="ac-textlink" to="/contact">Tell us what you’re building →</Link>
          </p>
          <p className="ac-reveal" style={{ marginTop: 18 }}>
            <Link className="ac-textlink" to="/film">Also a practice of its own: film finance, deal structuring, and production →</Link>
          </p>
        </div>
      </section>
    </SitePage>
  );
}
